import type { Landmark } from '../../../data/types';
import type { Ring, Vec2 } from '../../../geo/types';
import { corners } from './genericBlock';
import { toLayout, type PlanFrame } from './planFrame';
import readings from './cellezustersPlan.json';

export const CELLEZUSTERS = readings;
export type CellePart = typeof readings.chapel | (typeof readings.ranges)[number];

export function cellezustersFrame(landmark: Landmark): PlanFrame {
  const x = landmark.params['anchorX'],
    y = landmark.params['anchorY'];
  if (typeof x !== 'number' || typeof y !== 'number')
    throw new Error('Cellezusters lacks its component anchor');
  return { origin: [x, y], heading: landmark.heading, ring: [] };
}

export function cellePartFrame(plan: PlanFrame, part: CellePart): PlanFrame {
  return {
    origin: toLayout(plan, [part.u, part.v]),
    heading: plan.heading + (part.angleDeg * Math.PI) / 180,
    ring: [],
  };
}

export function cellePartRect(part: CellePart) {
  return { u0: -part.length / 2, u1: part.length / 2, v0: -part.width / 2, v1: part.width / 2 };
}

export function celleCourtRing(): Ring {
  return readings.court.map((p): Vec2 => [p[0]!, p[1]!]);
}

/** Leave the hospital's physical lower passage out of its collision and foundations. */
export function cellezustersObstacles(landmark: Landmark): Ring[] {
  const plan = cellezustersFrame(landmark);
  return [readings.chapel, ...readings.ranges].flatMap((part) => {
    const frame = cellePartFrame(plan, part),
      rect = cellePartRect(part);
    const passage = 'passage' in part && typeof part.passage === 'number' ? part.passage : 0;
    const rooms = passage
      ? [
          { ...rect, u1: -passage / 2 },
          { ...rect, u0: passage / 2 },
        ]
      : [rect];
    return rooms.map((room) => corners(room).map((p) => toLayout(frame, p)));
  });
}

export function celleGardenRects() {
  const g = readings.garden,
    rects = [];
  for (let row = 0; row < g.rows; row++)
    for (let col = 0; col < g.columns; col++) {
      const du = (g.u1 - g.u0) / g.columns,
        dv = (g.v1 - g.v0) / g.rows;
      rects.push({
        u0: g.u0 + col * du + 0.4,
        u1: g.u0 + (col + 1) * du - 0.4,
        v0: g.v0 + row * dv + 0.4,
        v1: g.v0 + (row + 1) * dv - 0.4,
      });
    }
  return rects;
}

export function cellezustersFloors(landmark: Landmark) {
  const plan = cellezustersFrame(landmark),
    hospital = readings.ranges.find((r) => r.id === readings.entryRange)!;
  return [
    ...celleGardenRects().map((rect) => ({
      ring: corners(rect).map((p) => toLayout(plan, p)),
      height: 0.035,
    })),
    { ring: celleCourtRing().map((p) => toLayout(plan, p)), height: 0.03 },
    {
      ring: corners(readings.entry).map((p) => toLayout(cellePartFrame(plan, hospital), p)),
      height: 0.03,
    },
  ];
}
